import { Injectable } from '@angular/core';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  StorageReference,
  uploadBytesResumable,
} from '@angular/fire/storage';

const RESIZE_SUFFIX = '_2560x1440';
const RESIZE_POLL_TIMEOUT_MS = 30000;
const RESIZE_POLL_INTERVAL_MS = 500;

export interface UploadResult {
  url: string;
  path: string;
  name: string;
  contentType: string;
  size: number;
}

export interface UploadOptions {
  /** Prepend `${Date.now()}_` to the filename to avoid collisions. Default: true. */
  prefixTimestamp?: boolean;
  /** Sanitize the filename (replace non-safe chars with `_`). Default: true. */
  sanitizeFilename?: boolean;
  /**
   * Wait for the Firebase Resize Images extension to produce the
   * `_2560x1440` variant and return its URL. When omitted, defaults to
   * `true` for image content types and `false` otherwise.
   */
  resizeAware?: boolean;
  /** Optional progress callback (0-100). */
  onProgress?: (percent: number) => void;
}

@Injectable({ providedIn: 'root' })
export class StorageUploadService {
  /**
   * Upload a single file under `${folder}/${filename}` and return the
   * final download URL. For images, awaits the Resize Images extension
   * and returns the URL of the resized variant — see {@link UploadOptions}.
   */
  async upload(
    folder: string,
    file: File | Blob,
    options: UploadOptions = {},
  ): Promise<UploadResult> {
    const {
      prefixTimestamp = true,
      sanitizeFilename = true,
      onProgress,
    } = options;

    const originalName = (file instanceof File ? file.name : 'upload') || 'upload';
    const safeName = sanitizeFilename ? this.sanitize(originalName) : originalName;
    const filename = prefixTimestamp ? `${Date.now()}_${safeName}` : safeName;
    const path = `${this.normalizeFolder(folder)}/${filename}`;
    const contentType =
      (file instanceof File ? file.type : (file as Blob).type) || 'application/octet-stream';
    const isImage = contentType.startsWith('image/');
    const useResize = options.resizeAware ?? isImage;
    const size = file.size;

    const storage = getStorage();
    const storageRef = ref(storage, path);

    try {
      const task = uploadBytesResumable(storageRef, file, { contentType });

      if (onProgress) {
        task.on('state_changed', (snap) => {
          if (snap.totalBytes > 0) {
            onProgress((snap.bytesTransferred / snap.totalBytes) * 100);
          }
        });
      }

      const snapshot = await task;
      const url = useResize
        ? await this.getResizedUrl(snapshot.ref)
        : await getDownloadURL(snapshot.ref);

      return {
        url,
        path,
        name: originalName,
        contentType,
        size,
      };
    } catch (err) {
      console.error('[StorageUpload] upload failed', { path, error: err });
      throw err;
    }
  }

  /**
   * Delete a file by its `gs://`/`https://` download URL or by path.
   * Silently swallows "object-not-found" errors.
   */
  async delete(urlOrPath: string): Promise<void> {
    if (!urlOrPath) return;
    const storage = getStorage();
    try {
      const r = ref(storage, urlOrPath);
      await deleteObject(r);
    } catch (err: any) {
      if (err?.code === 'storage/object-not-found') return;
      console.error('[StorageUpload] delete failed', { urlOrPath, error: err });
      throw err;
    }
  }

  private async getResizedUrl(originalRef: StorageReference): Promise<string> {
    const originalUrl = await getDownloadURL(originalRef);
    const resizedUrl = this.insertResizeSuffix(originalUrl);
    return this.waitForResized(resizedUrl);
  }

  private async waitForResized(urlWithSuffix: string): Promise<string> {
    const storage = getStorage();
    const start = Date.now();
    while (Date.now() - start < RESIZE_POLL_TIMEOUT_MS) {
      try {
        const r = ref(storage, urlWithSuffix);
        return await getDownloadURL(r);
      } catch {
        await this.delay(RESIZE_POLL_INTERVAL_MS);
      }
    }
    console.warn('[StorageUpload] resize wait timed out, returning suffixed URL', {
      url: urlWithSuffix,
    });
    return urlWithSuffix;
  }

  private insertResizeSuffix(url: string): string {
    const queryStart = url.indexOf('?');
    const head = queryStart >= 0 ? url.substring(0, queryStart) : url;
    const tail = queryStart >= 0 ? url.substring(queryStart) : '';
    const lastDot = head.lastIndexOf('.');
    if (lastDot < 0) return url;
    return head.substring(0, lastDot) + RESIZE_SUFFIX + head.substring(lastDot) + tail;
  }

  private sanitize(name: string): string {
    return name.replace(/[^a-zA-Z0-9._-]/g, '_');
  }

  private normalizeFolder(folder: string): string {
    return folder.replace(/^\/+|\/+$/g, '');
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
